import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { isInReflowRow } from 'editor/src/store/design/util/sharedTextFields';
import {
  buildTextStyleElementUpdate,
  getTextStyle,
  getTextStyleFillWrite,
  getTextStyleMembers,
  isTextStyleProtected,
} from 'editor/src/store/design/util/textStyles';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import type { RootState } from 'editor/src/store/index';

import updateHiddenElementsOperation from './updateHiddenElementsOperation';

/**
 * Rewrites every text following the style with the style's current values, except texts the
 * user may not edit (see isTextStyleProtected): they keep their look and their link.
 *
 * Dispatches the plain update action and saves no undo step of its own: it
 * runs inside the caller's batch, so a style edit and all the texts it moves
 * are one undo entry.
 */
const applyTextStyleToMembersOperation = (styleId: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
  const { designData } = getState().design;
  const { cmykOnly } = getState().hostSettings;
  const style = getTextStyle(designData, styleId);
  if (!style) {
    return;
  }

  const spreadsToReflow = new Set<number>();
  getTextStyleMembers(designData, styleId).forEach(({ element, address }) => {
    if (isTextStyleProtected(getState(), element)) {
      return;
    }
    dispatch(
      updateMediaElementAction({
        elementAddress: address,
        elementUpdate: buildTextStyleElementUpdate(
          element,
          style,
          getTextStyleFillWrite(designData, cmykOnly, style, address.spreadIndex, element.uuid),
        ),
      }),
    );
    if (isInReflowRow(designData, address.spreadIndex, element.uuid)) {
      spreadsToReflow.add(address.spreadIndex);
    }
  });

  // Once per spread, after all its members moved: a row packs from every member's width.
  spreadsToReflow.forEach((spreadIndex) => dispatch(updateHiddenElementsOperation(spreadIndex)));
};

export default applyTextStyleToMembersOperation;
